import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';

import { send } from 'loot-core/platform/client/fetch';
import { type TransactionEntity } from 'loot-core/types/models';

import { useBudgetedAccounts } from '../../hooks/useBudgetedAccounts';
import { useDemoSite } from '../../hooks/useDemoSite';
import { AnimatedLoading } from '../../icons/AnimatedLoading';
import { useResponsive } from '../../ResponsiveProvider';
import { theme, styles } from '../../style';
import { Block } from '../common/Block';
import { View } from '../common/View';
import { Page } from '../Page';
import { usePageTitle } from '../PageTitleContext';
import { BudgetSummaryCard } from '../reports/reports/BudgetSummaryCard';
import { CashFlowCard } from '../reports/reports/CashFlowCard';
import { SpendingByCategoryCard } from '../reports/reports/SpendingByCategoryCard';

import DashboardAccounts from './accounts/DashboardAccounts';
import BillingDashboard from './billing/BillingDashboard';
import dashboardStyles from './dashboard.module.scss';
import DashboardTransactions from './latest-transactions/DashboardTransactions';

const Dashboard: React.FC = () => {
  const budgetedAccounts = useBudgetedAccounts();
  const { setPageTitle } = usePageTitle();
  const { isWideWidth } = useResponsive();
  const { isDemoBudgetFile } = useDemoSite();

  const linkedAccounts = useSelector(state => state.account.linkedAccounts);
  const [transactions, setTransactions] = useState<TransactionEntity[] | null>(
    null,
  );

  useEffect(() => {
    setPageTitle('Dashboard');

    getLatestTransactions();
  }, []);

  const accounts = useMemo(() => {
    if (budgetedAccounts && linkedAccounts) {
      let accounts = [];
      if (budgetedAccounts.length) {
        accounts = budgetedAccounts.map(a => {
          const linkedAccount = a.account_id
            ? linkedAccounts.find(la => la.account_id === a.id)
            : null;
          return {
            id: a.id,
            accountId: linkedAccount?.account_id,
            name: a.name,
            status: linkedAccount?.status || null,
            isLocal: !a.account_id,
            syncTimestamp: linkedAccount?.lastSyncedAt || null,
            isClosed: !!a.closed,
          };
        });
      }
      return accounts;
    } else {
      return null;
    }
  }, [budgetedAccounts, linkedAccounts]);

  const getLatestTransactions = async () => {
    const trans = await send('get-latest-transactions');
    setTransactions(trans || []);
  };

  if (!transactions || !accounts) {
    return (
      <View
        style={{
          flex: 1,
          gap: 20,
          justifyContent: 'center',
          alignItems: 'center',
          ...styles.delayedFadeIn,
        }}
      >
        <Block style={{ marginBottom: 20, fontSize: 18 }}>
          Loading dashboard...
        </Block>
        <AnimatedLoading width={25} color={theme.pageTextDark} />
      </View>
    );
  }

  return (
    <Page
      title=""
      headerStyle={{ display: 'none' }}
      style={{ minHeight: 'auto', flex: 1 }}
      childrenContainerProps={{ style: { minHeight: 'auto', flex: 1 } }}
    >
      <AutoSizer>
        {({ width }) => (
          <div className={dashboardStyles.dashboard} style={{ width }}>
            {/* <OnboardingChecklist
              hasTransaction={!!transactions.length}
              hasAccount={!!budgetedAccounts.length}
            /> */}
            <div
              style={{
                display: 'flex',
                flex: 1,
                flexDirection: !isWideWidth ? 'column' : 'row',
                gap: 32,
              }}
            >
              <div className="column" style={{ alignItems: 'flex-end' }}>
                <div className="card chart">
                  <BudgetSummaryCard
                    showViewAll={true}
                    cardStyles={{
                      height: '287.5px',
                      borderRadius: '6px',
                      boxShadow: '0 1px 2px #9594A8',
                      border: 'none',
                      flex: 'unset',
                      margin: 0,
                    }}
                  />
                </div>
                <div className="card chart">
                  <CashFlowCard
                    showViewAll={true}
                    cardStyles={{
                      height: '287.5px',
                      borderRadius: '6px',
                      boxShadow: '0 1px 2px #9594A8',
                      border: 'none',
                      flex: 'unset',
                      margin: 0,
                    }}
                  />
                </div>
                <div
                  className={`spending-by-category card chart ${accounts?.length > 10 ? 'align-with-accounts' : ''}`}
                >
                  <SpendingByCategoryCard
                    showViewAll={true}
                    cardStyles={{
                      borderRadius: '6px',
                      boxShadow: '0 1px 2px #9594A8',
                      border: 'none',
                    }}
                  />
                </div>
              </div>
              <div className="column">
                <DashboardTransactions transactions={transactions} />
                <DashboardAccounts accounts={accounts} />
              </div>
            </div>
            {!isDemoBudgetFile && <BillingDashboard />}
          </div>
        )}
      </AutoSizer>
    </Page>
  );
};
export default Dashboard;
